import { useTranslation } from 'react-i18next';
import ButtonIcon from '../../../../../components/buttons/ButtonIcon';
import {
    CleanIcon,
    IconTraffic, PersonChipBlack,
    Traffic,
} from '../../../../../components/buttons/IconButtons';
import CustomCard from '../../../../../components/CustomCard';
import { CustomSelect } from '../../../../../components/CustomSelect';
import { CustomTextField } from '../../../../../components/CustomTextField';
import CustomModal from '../../../../../components/Modal/CustomModal';
import CustomPhotoPickerMultiple from '../../../../../components/PhotoPicker/CustomPhotoPickerMultiple';
import { FILE_TYPE } from '../../../../../Helpers/fileType';
import { AddEditFineModalApp } from '../Functionality/AddEditFineModalApp';
import React from 'react';
import CustomDateTimePicker from '../../../../../components/CustomDateTimePicker';
import CustomAddChipSearch from '../../../../../components/CustomAddChipSearch';
import GeofencesMap from '../../Manage/SubComponents/GeofencesMap';
import {userListToLabelEmailPhoneValue} from "../../../../../Apis/functions";

/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * @Description
 * Modal for creating or editing a fine. Includes:
 * - Fine data form
 * - Interactive map (using OverlayMap)
 * - Upload evidence section
 * - Automatic overlay management inside the map component
 */
const AddEditFineModal = (props) => {
    const { show, onHide, setModified } = props;
    const { t } = useTranslation();

    const {
        item,
        handleChange,
        handleClear,
        emptyFields,
        handleAddEdit,
        photoPickerFile,
        setPhotoPickerFile,
        getGeofenceList,
        fineTypesAll,
        disabled,
        selectedUsers, setSelectedUsers,
        userTypeList,
        centerMap, geofence,
        locations, setLocations,
        speedUnit
    } = AddEditFineModalApp({
        show,
        onHide,
        setModified
    });

    return (
        <CustomModal
            show={show}
            onHide={onHide}
            size="lg"
            buttonConfirm="confirm"
            classNameBody="m-0 p-0"
            onConfirm={()=>handleAddEdit()}
            topLeftHeader={<>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <div><IconTraffic className="w-32px"/></div>
                        <p className="fs-24px font-color-general-950 ibl-font-w-600 ml-2">
                            {t('create_fine')}
                        </p>
                    </div>
                </div>
            </>}
        >
            <>
                <CustomCard classCard="card-border-8px" classContent="p-0">
                    <Traffic className="w-100" />

                    {item?.id === null &&
                        <div className="col-12 d-flex justify-content-end pt-2">
                            <ButtonIcon
                                className="btn-clean-secondary-600"
                                id="btn_aefm_clean_form"
                                Icon={CleanIcon}
                                title="clear_instances"
                                onClick={handleClear}
                            />
                        </div>
                    }

                    <div className="d-flex px-0 mt-3">
                        {/* Left column: inputs */}
                        <div className="col-6">
                            <div className="col-12">

                                <CustomSelect
                                    id="slt_aefm_geofence"
                                    name="geofence"
                                    value={item.geofence}
                                    keyName="id"
                                    trans="geofence"
                                    showNoSelectionOption={false}
                                    required={true}
                                    emptyFields={emptyFields}
                                    list={getGeofenceList}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="col-12">
                                <CustomSelect
                                    id="slt_aefm_fine_type"
                                    name="fineType"
                                    value={item.fineType}
                                    keyName="id"
                                    trans="fine_type"
                                    showNoSelectionOption={false}
                                    required={true}
                                    emptyFields={emptyFields}
                                    list={fineTypesAll}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="col-12">
                                <CustomDateTimePicker
                                    id={'dp_aefm_date_time'}
                                    name={'dateTime'}
                                    disabled={disabled}
                                    onChange={(e)=>{
                                        handleChange({ target: { value: e, name: 'dateTime' } });
                                    }}
                                    trans={'date_and_time'}
                                    value={item?.dateTime}
                                    required={true}
                                    disablePast={false}
                                    openTo={'day'}
                                    emptyFields={emptyFields}
                                    disableFuture={true}
                                />
                            </div>

                            <div className="col-12">
                                <CustomTextField
                                    id="txt_aefm_speed"
                                    name="speed"
                                    value={item.speed}
                                    trans="speed"
                                    type="number"
                                    onChange={handleChange}
                                    required={true}
                                    emptyFields={emptyFields}
                                    length={3}
                                    suffixText={speedUnit ? <p className={"ibl-font-w-600 text-uppercase"}>{speedUnit}</p>: undefined}
                                />
                            </div>

                            <div className='col-12'>
                                <CustomAddChipSearch
                                    id="txt_aefm_search_host"
                                    selectedList={selectedUsers}
                                    setSelectedList={setSelectedUsers}
                                    userTypeIdsSearch={userTypeList}
                                    disabled={!item?.geofence}
                                    label="user_to_fine"
                                    emptyFields={emptyFields}
                                    required={true}
                                    maxLength={1}
                                    reset={false}
                                    keyId={"userId"}
                                    completeWidth={true}
                                    showSubSection={true}
                                    sanitizeDataList={userListToLabelEmailPhoneValue}
                                    Icon={PersonChipBlack}
                                />
                            </div>
                        </div>

                        {/* Right column: map (OverlayMap replaces CafeteriaMap) */}
                        <div className="col-6">
                            <GeofencesMap
                                id={JSON.stringify(centerMap) + JSON.stringify(geofence?.geofenceId)}
                                key={JSON.stringify(centerMap) + JSON.stringify(geofence?.geofenceId)}
                                center={centerMap}
                                geofences={geofence ? [geofence]: []}
                                locations={locations}
                                geofenceTitle='defined_areas'
                                showTitle={false}
                                onMarkerPlaced={(pos) => {
                                    setLocations([{position: pos}])
                                }}
                                disabled={item?.geofence === null}
                                emptyFields={emptyFields && locations?.length <= 0}
                                classNameMap={"h-100"}
                            />
                        </div>
                    </div>

                    {/* Description and evidence section */}
                    <div className={`px-2 mb-2 mt-5`}>
                        <p className="text-center ibl-font-w-600 lh-base pb-2">
                            {t('please_provide_a_reason_for_the_fine_below').toUpperCase()}
                        </p>

                        <div className="col-12">
                            <CustomTextField
                                id="txt_aefm_description"
                                length={128}
                                name="description"
                                value={item.description}
                                trans="description"
                                type="text"
                                onChange={handleChange}
                                multiline
                                required
                                emptyFields={emptyFields}
                            />
                        </div>

                        <p className="text-center ibl-font-w-600 lh-base pb-2">
                            {t('attach_evidence_optional').toUpperCase()}
                        </p>

                        <div className="col-12 px-3 mb-3">
                            <CustomPhotoPickerMultiple
                                id='fp_aefm_fine_document'
                                photoPickerFile={photoPickerFile}
                                setPhotoPickerFile={setPhotoPickerFile}
                                accept={{ ...FILE_TYPE.IMAGE }}
                                showBtnDeleteItemGallery={true}
                                showBtnCamera={false}
                            />
                        </div>
                    </div>
                </CustomCard>
            </>
        </CustomModal>
    );
};

export default AddEditFineModal;
